import { Banner, CreateBannerDto, IBannerService, UpdateBannerDto } from "../../types/banner.types";
import { supabase } from '../../config/supabase';
import { SupabaseClient } from '@supabase/supabase-js';

export class BannerService implements IBannerService {
    client: SupabaseClient = supabase;
    
    private static instance: BannerService;

    public static getInstance(): BannerService {
        if (!BannerService.instance) {
            BannerService.instance = new BannerService();
        }
        return BannerService.instance;
    }
    private readonly TABLE_NAME = 'banners';

    /**
     * Get banners with pagination and optional filters
     */
    async getBanners(page: number = 1, limit: number = 10, filters?: Record<string, any>) {
        try {
            let query = this.client
                .from(this.TABLE_NAME)
                .select('*', { count: 'exact' });

            // Apply filters if provided
            if (filters) {
                // Filter implementation here if needed
                // Add more filters as needed
            }

            // Calculate offset for pagination
            const offset = (page - 1) * limit;

            // Order by index (nulls last) and then by created_at
            const { data, error, count } = await query
                .order('index', { ascending: true, nullsFirst: false })
                .order('created_at', { ascending: false })
                .range(offset, offset + limit - 1);

            if (error) {
                console.error('Error fetching banners:', error);
                throw error;
            }

            // Calculate total pages
            const totalPages = count ? Math.ceil(count / limit) : 0;

            return {
                data: data as Banner[],
                pagination: {
                    total: count || 0,
                    page,
                    limit,
                    totalPages
                }
            };
        } catch (error) {
            console.error('Error in getBanners:', error);
            throw error;
        }
    }

    /**
     * Get a banner by ID
     */
    async getBannerById(id: string): Promise<Banner | null> {
        try {
            const { data, error } = await this.client
                .from(this.TABLE_NAME)
                .select('*')
                .eq('id', id)
                .single();

            if (error) {
                console.error('Error fetching banner by ID:', error);
                throw error;
            }

            return data as Banner;
        } catch (error) {
            console.error('Error in getBannerById:', error);
            throw error;
        }
    }

    /**
     * Create a new banner
     */
    async createBanner(banner: CreateBannerDto): Promise<Banner> {
        try {
            const { data, error } = await this.client
                .from(this.TABLE_NAME)
                .insert([banner])
                .select()
                .single();

            if (error) {
                console.error('Error creating banner:', error);
                throw error;
            }

            return data as Banner;
        } catch (error) {
            console.error('Error in createBanner:', error);
            throw error;
        }
    }

    /**
     * Update an existing banner
     */
    async updateBanner(id: string, banner: UpdateBannerDto): Promise<Banner> {
        try {
            // Add updated_at
            const updatedBanner = {
                ...banner,
                updated_at: new Date().toISOString()
            };

            const { data, error } = await this.client
                .from(this.TABLE_NAME)
                .update(updatedBanner)
                .eq('id', id)
                .select()
                .single();

            if (error) {
                console.error('Error updating banner:', error);
                throw error;
            }

            return data as Banner;
        } catch (error) {
            console.error('Error in updateBanner:', error);
            throw error;
        }
    }

    /**
     * Delete a banner by ID
     */
    async deleteBanner(id: string): Promise<boolean> {
        try {
            const { error } = await this.client
                .from(this.TABLE_NAME)
                .delete()
                .eq('id', id);

            if (error) {
                console.error('Error deleting banner:', error);
                throw error;
            }

            return true;
        } catch (error) {
            console.error('Error in deleteBanner:', error);
            throw error;
        }
    }

    /**
     * Get the maximum index value
     */
    async getMaxIndex(): Promise<number> {
        try {
            const { data, error } = await this.client
                .from(this.TABLE_NAME)
                .select('index')
                .order('index', { ascending: false })
                .limit(1)
                .single();

            if (error && error.code !== 'PGRST116') { // PGRST116 is "no rows returned"
                console.error('Error getting max index:', error);
                throw error;
            }

            return (data?.index || 0) + 1;
        } catch (error) {
            console.error('Error in getMaxIndex:', error);
            throw error;
        }
    }

    /**
     * Update a banner's index
     */
    async updateBannerIndex(id: string, index: number): Promise<Banner> {
        try {
            const { data, error } = await this.client
                .from(this.TABLE_NAME)
                .update({ index, updated_at: new Date().toISOString() })
                .eq('id', id)
                .select()
                .single();

            if (error) {
                console.error('Error updating banner index:', error);
                throw error;
            }

            return data as Banner;
        } catch (error) {
            console.error('Error in updateBannerIndex:', error);
            throw error;
        }
    }
}
